"use client";

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { WorkspaceFile } from "@/lib/workspace-context";

interface EditorMetadataTagsProps {
  file: WorkspaceFile;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  className?: string;
}

/**
 * Etiquetas de metadata del documento.
 *  - Cada tag existente se muestra con un chip
 *  - Click en X la remueve
 *  - Click en "+ Tag" abre un input inline
 *  - Enter confirma, Escape cancela
 */
export function EditorMetadataTags({
  file,
  onAddTag,
  onRemoveTag,
  className,
}: EditorMetadataTagsProps) {
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim().toLowerCase();
    if (trimmed) {
      onAddTag(trimmed);
    }
    setInputValue("");
    setShowInput(false);
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2 mb-6 select-none", className)}>
      {file.tags?.map((tag) => (
        <span
          key={tag}
          className="px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1"
        >
          #{tag}
          <button
            onClick={() => onRemoveTag(tag)}
            className="hover:text-destructive transition-colors"
            aria-label={`Remover tag ${tag}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}

      {showInput ? (
        <form onSubmit={handleSubmit} className="inline-flex">
          <input
            type="text"
            placeholder="tag..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setInputValue("");
                setShowInput(false);
              }
            }}
            onBlur={() => {
              setTimeout(() => setShowInput(false), 100);
            }}
            className="w-16 bg-background border border-border rounded px-1.5 py-0.5 text-[10px] outline-none text-foreground focus:border-primary"
            autoFocus
            aria-label="Nueva etiqueta"
          />
        </form>
      ) : (
        <button
          onClick={() => setShowInput(true)}
          className="px-2 py-0.5 rounded-full bg-secondary border border-border text-[10px] font-bold text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all flex items-center gap-0.5"
          aria-label="Agregar etiqueta"
        >
          <Plus className="w-2.5 h-2.5" />
          Tag
        </button>
      )}
    </div>
  );
}
